<template>
  <transition
    appear-class="opacity-0"
    appear-active-class="transition-opacity duration-500 ease-out-quad"
    enter-class="opacity-0"
    enter-active-class="transition-opacity duration-500 ease-out-quad"
    leave-class="opacity-0"
    leave-active-class="transition-opacity duration-500 ease-out-quad"
    appear>
    <div
      class="
        absolute inset-0 z-above-search min-h-screen
        flex flex-col justify-between items-stretch
        bg-grey-lighter
      ">
      <div class="h-22 shadow bg-white px-5 flex justify-between items-center flex-shrink-0">
        <form
          class="relative w-full"
          :action="searchUrl">
          <input
            ref="input"
            v-model="value"
            type="search"
            name="q"
            class="type-content bg-grey-lighter px-6 py-4 pr-12 w-full appearance-none"
            :placeholder="placeholder"
            @keyup="querySearchAsync">
          <button
            type="submit"
            :disabled="value <= 0"
            class="
              absolute top-1/2 right-0
              transform -translate-y-1/2
              flex justify-center items-center
              w-12 h-12 z-default
            ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="19">
              <g
                data-name="icon search"
                fill="none"
                stroke="currentColor"
                stroke-miterlimit="10"
                stroke-width="2"
                transform="translate(1 1)">
                <circle cx="6.857" cy="6.857" r="6.857" />
                <path d="M13.714 13.714l3.489 3.489" />
              </g>
            </svg>
          </button>
        </form>
        <button
          class="block relative w-12 h-12 -mr-3 flex-shrink-0"
          @click="$parent.$emit('close')">
          <svg
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24">
            <!-- eslint-disable-next-line max-len -->
            <polygon points="4.22 21.19 2.81 19.78 10.59 12 2.81 4.22 4.22 2.81 12 10.59 19.78 2.81 21.19 4.22 13.41 12 21.19 19.78 19.78 21.19 12 13.41 4.22 21.19" fill="currentColor" />
          </svg>
        </button>
      </div>
      <div class="h-full overflow-y-scroll">
        <div class="px-6 pt-3 pb-12">
          <ul v-if="Object.keys(results).length > 0 && !results['no_content']">
            <li
              v-for="key in Object.keys(results)"
              :key="`results-${key}`"
              class="py-6">
              <span class="type-label font-bold">{{ results[key].label }}</span>
              <ul class="py-3">
                <li
                  v-for="result in results[key].items"
                  :key="`results-${key}-result-${result.value}`">
                  <a
                    :href="result.url"
                    class="
                      py-3
                      flex justify-between items-center
                      block text-grey-middle
                    ">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="highlight(result.value, value)" />
                    <div class="flex flex-no-wrap items-center ml-2">
                      <p
                        v-if="result.tag.title"
                        class="tag"
                        :class="`tag--${result.tag.color}`">
                        {{ result.tag.title }}
                      </p>
                      <svg
                        class="text-red inline-block ml-2"
                        xmlns="http://www.w3.org/2000/svg"
                        width="7.8"
                        height="12.7"
                        viewBox="0 0 7.8 12.7">
                        <path
                          fill="currentColor"
                          d="M1.4 12.7L0 11.3l4.9-5L0 1.4 1.4 0l6.4 6.3z" />
                      </svg>
                    </div>
                  </a>
                </li>
              </ul>
              <a
                v-if="!!results[key].link && results[key].link.label && results[key].link.url"
                :href="results[key].link.url"
                class="btn btn--black-border btn--block mt-3">
                {{ results[key].link.label }}
              </a>
            </li>
          </ul>
          <div
            v-else
            class="py-6">
            <template v-if="results['no_content']">
              <span class="type-label font-bold">{{ results['no_content'].label }}</span>
              <ul class="py-3">
                <li
                  v-for="result in results['no_content'].items"
                  :key="`results-no-content-result-${result.value}`"
                  class="mt-3 mr-3 inline-block">
                  <a :href="result.url" class="btn btn--black inline-block">
                    <span class="btn__label">
                      {{ result.value }}
                    </span>
                  </a>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  export default {
    props: {
      /**
       * Input placeholder text
       */
      placeholder: {
        type: String,
        required: true,
      },
      /**
       * Search for content on the given endpoint
       */
      endpoint: {
        type: String,
        required: true,
      },
      /**
       * Search Url
       */
      searchUrl: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        value: '',
        results: {},
      };
    },
    mounted() {
      this.$refs.input.focus();
      this.querySearchAsync();
    },
    methods: {
      highlight(string, query) {
        // eslint-disable-next-line no-useless-escape
        const esc = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        const reg = new RegExp(esc, 'ig');
        return string.replace(reg, '<strong class="text-black font-bold">$&</strong>');
      },
      async querySearchAsync() {
        await fetch(`${this.endpoint}?q=${this.value}`)
          .then((response) => response.json())
          .then((data) => {
            this.results = data;
          });
      },
    },
  };
</script>

<style>
  [type="search"]::-webkit-search-cancel-button,
  [type="search"]::-webkit-search-decoration {
    -webkit-appearance: none;
    appearance: none;
  }
</style>
